export interface CruiseOfferPriceSelectionOptions {
  cabinTypes?: string
  minPrice?: number
  maxPrice?: number
  sortBy?: string
  cruisePriceByNight: boolean
  shouldUseLuxPlusPriceSelection: boolean
  departureMemberPricing?: App.Cruises.CruiseOfferMemberPricing['departures']
}

export interface CruiseLowestPriceSelection {
  departureId: string
  price?: App.CruisePriceDetails | App.LowestPriceWithCategory
  memberPrice?: number
  memberPriceType?: App.Cruises.CruiseMemberPriceType
}

export interface CruiseLowestBundlePriceSelection {
  price: App.CruisePriceDetails
}

type CruisePriceFilterOptions = Pick<CruiseOfferPriceSelectionOptions,
  'cabinTypes' | 'minPrice' | 'maxPrice' | 'sortBy' | 'cruisePriceByNight'>

export function getCruiseMemberPriceByCategory(
  departureMemberPricingByCategory: Record<string, App.Cruises.CruiseOfferMemberPriceByCategory> | undefined,
  cabinCategory: string | undefined,
): App.Cruises.CruiseOfferMemberPriceByCategory | undefined {
  if (!departureMemberPricingByCategory || !cabinCategory) {
    return undefined
  }
  return departureMemberPricingByCategory[cabinCategory.toLowerCase()]
}

function getLowestLuxPlusPriceSelection(
  offer: App.CruiseOffer,
  departureMemberPricing: App.Cruises.CruiseOfferMemberPricing['departures'] | undefined,
  cabinTypes: string | undefined,
): CruiseLowestPriceSelection | undefined {
  return departureMemberPricing?.reduce<CruiseLowestPriceSelection | undefined>((lowestSelection, departure) => {
    return Object.values(departure.lowestPricesByCategory).reduce<CruiseLowestPriceSelection | undefined>((
      lowestCategorySelection,
      memberPriceDetails,
    ) => {
      const cabinCategory = memberPriceDetails.cabinCategory.toLowerCase()
      const cabinTypesFilter = cabinTypes?.toLowerCase()

      if (cabinTypesFilter && cabinCategory !== cabinTypesFilter) {
        return lowestCategorySelection
      }

      const price = offer.lowestDeparturePricesByCategory[departure.id]?.[cabinCategory]

      if (!price || !memberPriceDetails.memberPrice) {
        return lowestCategorySelection
      }

      if ((lowestCategorySelection?.memberPrice ?? Number.MAX_SAFE_INTEGER) < memberPriceDetails.memberPrice) {
        return lowestCategorySelection
      }

      return {
        departureId: departure.id,
        price,
        memberPrice: memberPriceDetails.memberPrice,
        memberPriceType: memberPriceDetails.memberPriceType,
      }
    }, lowestSelection)
  }, undefined)
}

export function getCruiseLowestPriceSelection(
  offer: App.CruiseOffer,
  {
    cabinTypes,
    minPrice,
    maxPrice,
    sortBy,
    cruisePriceByNight,
    shouldUseLuxPlusPriceSelection,
    departureMemberPricing,
  }: CruiseOfferPriceSelectionOptions,
): CruiseLowestPriceSelection {
  // Start with the overall lowest standard price already calculated by the API.
  let departureId = offer.mainDepartureDetails.id
  let lowestOffer: App.CruisePriceDetails | App.LowestPriceWithCategory | undefined =
    offer.mainDepartureDetails.lowestOverallPriceDetails

  // A selected cabin uses its category price instead of the overall price.
  if (cabinTypes) {
    lowestOffer = offer.mainDepartureDetails.lowestPriceDetailsByCategory[cabinTypes]
  }

  // Price filters or descending order require searching across departures.
  const isSortDesc = sortBy === 'price.desc'

  if (minPrice || maxPrice || isSortDesc) {
    const allDeparturePrices: Array<{
      departureId: string
      price: App.LowestPriceWithCategory
    }> = Object.entries(offer.lowestDeparturePricesByCategory)
      .flatMap(([candidateDepartureId, categoryPrices]) =>
        Object.values(categoryPrices).map((price) => ({
          departureId: candidateDepartureId,
          price,
        })),
      )

    const qualifying = getFilteredCruisePriceSelections(allDeparturePrices, offer.duration, {
      cabinTypes,
      minPrice,
      maxPrice,
      sortBy,
      cruisePriceByNight,
    })

    if (qualifying.length > 0) {
      lowestOffer = qualifying[0].price
      departureId = qualifying[0].departureId
    }
  }

  // LuxPlus searches select the lowest member price and its matching standard rate.
  if (shouldUseLuxPlusPriceSelection) {
    const lowestLuxPlusPriceSelection = getLowestLuxPlusPriceSelection(offer, departureMemberPricing, cabinTypes)

    if (lowestLuxPlusPriceSelection) {
      return lowestLuxPlusPriceSelection
    }
  }

  return {
    departureId,
    price: lowestOffer,
  }
}

export function getCruiseLowestBundlePriceSelection(
  offer: App.CruiseOffer,
  options: CruisePriceFilterOptions,
): CruiseLowestBundlePriceSelection | undefined {
  const { cabinTypes, minPrice, maxPrice, sortBy } = options

  // Start with the overall lowest bundle price already calculated by the API.
  let price = offer.mainDepartureDetails.lowestBundleOverallPriceDetails

  // A selected cabin requires a bundle price for that category.
  if (cabinTypes) {
    price = offer.mainDepartureDetails.lowestBundlePriceDetailsByCategory?.[cabinTypes]
  }

  // Search across departures only when a price filter or descending order is active.
  if (minPrice || maxPrice || sortBy === 'price.desc') {
    const prices = Object.entries(offer.lowestBundleDeparturePricesByCategory ?? {})
      .flatMap(([departureId, categoryPrices]) =>
        Object.values(categoryPrices).map(price => ({ departureId, price })),
      )

    price = getFilteredCruisePriceSelections(prices, offer.duration, options)[0]?.price
  }

  // Missing or out-of-range bundle prices must not fall back to a standard price.
  return price ? { price } : undefined
}

function getFilteredCruisePriceSelections<Price extends App.CruisePriceDetails | App.LowestPriceWithCategory>(
  selections: Array<{ departureId: string, price: Price }>,
  duration: number,
  { cabinTypes, minPrice, maxPrice, sortBy, cruisePriceByNight }: CruisePriceFilterOptions,
): Array<{ departureId: string, price: Price }> {
  // Apply price filters in the same unit displayed to the customer.
  const priceForComparison = ({ price }: { price: Price }) => cruisePriceByNight
    ? Math.round(price.total / duration)
    : price.total

  return selections
    .filter(({ price }) => {
      // Keep only the selected cabin and prices within the requested range.
      if (cabinTypes && price.cabinCategory !== cabinTypes) return false
      const priceForFilter = priceForComparison({ price })
      if (minPrice && priceForFilter < minPrice) return false
      if (maxPrice && priceForFilter > maxPrice) return false
      return true
    })
    .sort((left, right) => {
      // Lowest price first by default; highest first for descending price order.
      const sortOrder = sortBy === 'price.desc' ? -1 : 1
      return (priceForComparison(left) - priceForComparison(right)) * sortOrder
    })
}
